"use client";

import {
  Add as AddIcon,
  CloudDownload as CloudDownloadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import EditBonusCardDialog from "./EditBonusCardDialog";
import NewBonusCardDialog from "./NewBonusCardDialog";

const CardManagement = ({ provider }) => {
  const [viewMode, setViewMode] = useState("list");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [isNewCardDialogOpen, setIsNewCardDialogOpen] = useState(false);
  const [isEditCardDialogOpen, setIsEditCardDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const cardsPerPage = 3; // Show 3 cards per page to match Operating Hours height
  const branchState = useSelector((state) => state.branches || {});
  const branchesFromStore = branchState.list || [];
  useEffect(() => {
    if (provider) {
      fetchCardsForVendor();
    }
  }, [provider]);

  const fetchCardsForVendor = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bonus_cards/get_cards_by_provider_id/${provider?._id}/true`,
        {
          headers: {
            Accept: "application/json, text/plain, */*",
          },
        }
      );
      const data = await response.json();
      if (data.status && Array.isArray(data.result)) {
        setCards(data.result);
      } else {
        //console.error("Invalid data structure:", data)
      }
    } catch (error) {
      console.error("Error fetching cards:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCards = cards.filter(
    (card) =>
      card.details.toLowerCase().includes(search.toLowerCase()) ||
      card.status.toLowerCase().includes(search.toLowerCase())
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredCards.length / cardsPerPage);
  const currentCards = filteredCards.slice(
    (page - 1) * cardsPerPage,
    page * cardsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Reset to first page when search changes
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleNewCardSave = (cardData) => {
    console.log("New card data:", cardData);
    setIsNewCardDialogOpen(false);
    fetchCardsForVendor();
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setIsEditCardDialogOpen(true);
  };

  const handleEditCardSave = async (updatedCardData) => {
    setFetching(true);
    try {
      let imageUrl;
      if (updatedCardData.imageFile) {
        const formData = new FormData();
        formData.append("file", updatedCardData.imageFile);

        const imageUploadResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/uploadFile`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        imageUrl = imageUploadResponse.data.url;
      }

      const dataToBeUpdated = {
        _id: updatedCardData._id,
        branchId: updatedCardData.branchId,
        maxPoints: updatedCardData.maxPoints,
        status: updatedCardData.status,
        details: updatedCardData.description,
        validUntil: moment(updatedCardData.validUntil).unix(),
      };

      if (imageUrl) {
        dataToBeUpdated.image = imageUrl;
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/bonus_cards/update_card`,
        dataToBeUpdated
      );
      setIsEditCardDialogOpen(false);
      fetchCardsForVendor();
    } catch (error) {
      console.error("Error updating card:", error);
      alert("Error updating card. Please try again.");
    } finally {
      setFetching(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    setFetching(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/bonus_cards/delete_card/${cardId}`
      );
      fetchCardsForVendor();
    } catch (error) {
      console.error("Error deleting card:", error);
      alert("Error deleting card. Please try again.");
    } finally {
      setFetching(false);
    }
  };

  const handleExportClick = () => {
    setIsExportDialogOpen(true);
  };

  const handleExportClose = () => {
    setIsExportDialogOpen(false);
    setSelectedMonth("");
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  const handleExport = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bonus_cards/getCardRedemptionReports`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            month: selectedMonth,
            id: provider?._id,
          }),
        }
      );

      if (!response.ok) throw new Error("Export failed");

      // Convert to blob (file data)
      const blob = await response.blob();

      // Create temporary download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "redemption-report.pdf"; // default filename
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      setIsSnackbarOpen(true);
      handleExportClose();
    } catch (error) {
      console.error("Error exporting report:", error);
      alert("Failed to export report. Please try again.");
    }
  };

  const handleVouchersExport = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/vouchers_mobile/getReportOfVocuhersCalimed?pid=${provider?._id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Export failed");

      // Convert to blob (file data)
      const blob = await response.blob();

      // Create temporary download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "redemption-report.pdf"; // default filename
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      setIsSnackbarOpen(true);
 
    } catch (error) {
      console.error("Error exporting report:", error);
      alert("Failed to export report. Please try again.");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: 800, mx: "auto" }}>
        <Box
          sx={{
            height: "calc(100vh - 200px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            {/* <TextField
              size="small"
              placeholder="Search cards..."
              value={search}
              onChange={handleSearchChange}
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            /> */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsNewCardDialogOpen(true)}
            >
              Add New Card
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={handleExportClick}
            >
              Export Report
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={handleVouchersExport}
            >
              Export Voucher Report
            </Button>{" "}
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", mb: 2 }}>
            <Stack spacing={2}>
              {loading ? (
                <Typography>Loading cards...</Typography>
              ) : currentCards.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 200,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No cards found
                  </Typography>
                </Paper>
              ) : (
                currentCards.map((card) => (
                  <Paper
                    key={card._id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {/* QR Code Image */}
                      <Box
                        sx={{
                          width: 100,
                          height: 100,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={
                            card.image ||
                            "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/image-NAcAIkTvHr39l0eiLSeZ4jiCmNKiXW.png"
                          }
                          alt={`QR Code for ${card.details}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>

                      {/* Card Details */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle1" noWrap>
                            {card.details}
                          </Typography>
                          <Chip
                            label={card.status}
                            color={
                              card.status === "enabled" ? "success" : "default"
                            }
                            size="small"
                          />
                          <Box sx={{ ml: "auto" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditCard(card)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteCard(card._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Max Points: {card.maxPoints}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Valid Until:{" "}
                          {moment(card.validUntil * 1000).format("DD-MMM-YYYY")}
                        </Typography>
                      </Box>
                    </Box>

                    {card.branchDetails != null && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Branch Name: {card.branchDetails.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Address:
                          {card.branchDetails.address}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Lat, Lng:
                          {card.branchDetails.latitude},{" "}
                          {card.branchDetails.longitude}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                ))
              )}
            </Stack>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pb: 2,
                bgcolor: "background.paper",
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Box>

        <NewBonusCardDialog
          provider={provider}
          branches={branchesFromStore}
          open={isNewCardDialogOpen}
          onClose={() => setIsNewCardDialogOpen(false)}
          onSave={handleNewCardSave}
        />

        <EditBonusCardDialog
          provider={provider}
          branches={branchesFromStore}
          open={isEditCardDialogOpen}
          onClose={() => setIsEditCardDialogOpen(false)}
          onSave={handleEditCardSave}
          card={editingCard}
        />

        {fetching && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 9999,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Dialog open={isExportDialogOpen} onClose={handleExportClose}>
          <DialogTitle>Export Card Redemption Report</DialogTitle>
          <DialogContent>
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              fullWidth
              displayEmpty
              inputProps={{ "aria-label": "Select month" }}
            >
              <MenuItem value="" disabled>
                Select a month
              </MenuItem>
              <MenuItem value="1">January</MenuItem>
              <MenuItem value="2">February</MenuItem>
              <MenuItem value="3">March</MenuItem>
              <MenuItem value="4">April</MenuItem>
              <MenuItem value="5">May</MenuItem>
              <MenuItem value="6">June</MenuItem>
              <MenuItem value="7">July</MenuItem>
              <MenuItem value="8">August</MenuItem>
              <MenuItem value="9">September</MenuItem>
              <MenuItem value="10">October</MenuItem>
              <MenuItem value="11">November</MenuItem>
              <MenuItem value="12">December</MenuItem>
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleExportClose}>Cancel</Button>
            <Button onClick={handleExport} disabled={!selectedMonth}>
              Export
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={isSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setIsSnackbarOpen(false)}
          message="Report exported successfully"
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CardManagement;
